import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUser, updateProfile } from "../redux/slices/profileSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../api/axios";
import { readStoredJson } from "../utilities/session";

const ProfilePage = ({ handleLogout }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: reduxUser } = useSelector((state) => state.profile);
  const [localUser, setLocalUser] = useState(() => {
    return readStoredJson("user", null);
  });
  const user = reduxUser || localUser;
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    email: "",
    mobile_number: "",
    voter_id: "",
    role: "",
    status: "",
    profile_photo_path: "",
  });
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const googleUser = readStoredJson("googleUser", null);

  useEffect(() => {
    dispatch(fetchUser());
  }, [dispatch]);

  useEffect(() => {
    if (reduxUser) setLocalUser(reduxUser);
  }, [reduxUser]);

  useEffect(() => {
    if (user) {
      setEditData({
        name: user.name || "",
        email: user.email || "",
        mobile_number: user.mobile_number || "",
        voter_id: user.voter_id || "",
        role: user.role || "",
        status: user.status || "",
        profile_photo_path: user.profile_photo_path || "",
      });

      setPreviewImage(
        user.profile_photo_path
          ? `${api.defaults.baseURL}/${user.profile_photo_path}`
          : googleUser?.picture || "assets/images/users/avatar-1.jpg"
      );
      return;
    }

    // Reason: keep /profile while token exists and fetchUser is in flight; only leave when logged out
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, googleUser?.picture]);

  const mobileRegex = /^01[3-9]\d{8}$/;
  const voterRegex = /^\d{10,17}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditData({ ...editData, profile_photo_path: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!editData.name.trim()) newErrors.name = "Name is must required!";
    if (!editData.email.trim()) newErrors.email = "Email is required!";
    else if (!/\S+@\S+\.\S+/.test(editData.email))
      newErrors.email = "Invalid email format!";
    if (!editData.mobile_number.trim())
      newErrors.mobile_number = "Mobile number is required!";
    else if (!mobileRegex.test(editData.mobile_number))
      newErrors.mobile_number = "Enter a valid mobile number!";
    if (!editData.voter_id.trim()) newErrors.voter_id = "Voter ID is required!";
    else if (!voterRegex.test(editData.voter_id))
      newErrors.voter_id = "Voter ID must be 10 to 17 digits long!";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = () => {
    if (!validate()) return;
    dispatch(updateProfile({ editData, userId: user.id }));
    setShowModal(false);
  };

  // Derive company-role pairs from company_roles (preferred), falling back to
  // roles + companies arrays for older payloads that lack the nested relation.
  const resolveCompanyRoles = () => {
    const nested = user?.company_roles || user?.companyRoles;
    if (nested && nested.length > 0) return nested;

    // Fallback: reconstruct from belongsToMany arrays
    const roles = user?.roles || [];
    const companies = user?.companies || [];
    if (!roles.length) return [];

    return roles.map((r) => {
      const company = companies.find((c) => c.id === r?.pivot?.company_id) || null;
      return {
        id: r?.pivot?.company_id,
        role: { id: r.id, name: r.name },
        company: company ? { id: company.id, name: company.name } : null,
      };
    });
  };

  const companyRoles = resolveCompanyRoles();

  if (!user) {
    if (localStorage.getItem("token")) {
      return <div className="p-4">Loading profile...</div>;
    }
    return null;
  }

  const profileIncomplete =
    !!user &&
    (!String(user.mobile_number ?? "").trim() || !String(user.voter_id ?? "").trim());

  return (
    <>
      <div className="wrapper">
        <div className="container-fluid">
          <div className="container py-5">
            {profileIncomplete && (
              <div className="alert alert-warning" role="alert">
                Please add your mobile number and voter ID to complete your profile.
              </div>
            )}
            <div
              className="card border-0 shadow-lg p-4"
              style={{
                borderRadius: "20px",
                background: "linear-gradient(145deg, #ffffff, #f3f6ff)",
              }}
            >
              <div className="row align-items-center">
                {/*  Left Profile Photo Card */}
                <div className="col-md-4 text-center">
                  <div
                    className="p-4 shadow-sm"
                    style={{
                      borderRadius: "10px",
                      background: "#fff",
                    }}
                  >
                    <img
                      src={previewImage}
                      alt="User Avatar"
                      className="rounded-circle shadow"
                      width="180"
                      
                      style={{ border: "2px solid white" ,height:"180px"}}
                    />

                    <h4 className="mt-3 fw-bold ">{user.name}</h4>
                    {/* <small className="text-muted">{user.email}</small> */}
                    <div className="d-flex align-items-center w-100 justify-content-center">
                      {" "}
                      <button
                        className="btn btn-primary btn-sm mt-4 px-4 py-2 shadow-sm "
                        style={{ borderRadius: "30px" }}
                        onClick={() => setShowModal(true)}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </div>
                </div>

                {/* 🟩 Right Information Side with Beautiful Card Sections */}
                <div className="col-md-8">
                  <div
                    className="p-4 mb-4 shadow-sm"
                    style={{ background: "#fff", borderRadius: "20px" }}
                  >
                    <h5 className="fw-bold mb-3">Personal Details</h5>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-muted">Name</label>
                        <input
                          className="form-control"
                          value={user.name}
                          readOnly
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label text-muted">Email</label>
                        <input
                          className="form-control"
                          value={user.email}
                          readOnly
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label text-muted">
                          Mobile Number
                        </label>
                        <input
                          className="form-control"
                          placeholder="please enter your Mobile Number"
                          value={user.mobile_number || ""}
                          readOnly
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label text-muted">
                          NID / Voter ID
                        </label>
                        <input
                          className="form-control"
                          value={user.voter_id}
                          placeholder="please enter your NID"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="p-4 shadow-sm"
                    style={{ background: "#fff", borderRadius: "20px" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="fw-bold mb-0">Roles & Companies</h5>
                      <span className={`badge ${user.status === 'active' ? 'bg-success' : 'bg-secondary'} px-3 py-2 rounded-pill text-capitalize`}>
                        {user.status || 'Active'}
                      </span>
                    </div>

                    <div className="row mt-3">
                      {companyRoles.length > 0 ? (
                        companyRoles.map((cr, index) => (
                          <div key={index} className="col-md-6 mb-3">
                            <div className="card h-100 border-0 shadow-sm" style={{ backgroundColor: "#f8f9fa", borderRadius: "15px", borderLeft: "4px solid #0d6efd" }}>
                              <div className="card-body py-3">
                                <h6 className="card-title text-primary fw-bold mb-1 text-uppercase" style={{ fontSize: "0.9rem" }}>
                                  {cr.role?.name || 'Unknown Role'}
                                </h6>
                                <p className="card-text text-muted mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                                  🏢 {cr.company?.name || 'Unknown Company'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12">
                          <p className="text-muted fst-italic">No roles assigned yet.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*  Edit Modal Styled */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Update Profile</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <div className="text-center mb-3">
              <img
                src={previewImage}
                width="110"
                height="110"
                className="rounded-circle shadow"
                alt="Preview"
              />

              <Form.Group controlId="formFile" className="mt-3">
                <Form.Control type="file" onChange={handleImageChange} />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={editData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Mobile Number</Form.Label>
              <Form.Control
                type="text"
                name="mobile_number"
                value={editData.mobile_number}
                onChange={handleChange}
                placeholder="please enter your Mobile Number"
                isInvalid={!!errors.mobile_number}
              />
              <Form.Control.Feedback type="invalid">
                {errors.mobile_number}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">NID / Voter ID</Form.Label>
              <Form.Control
                type="text"
                name="voter_id"
                value={editData.voter_id}

                placeholder="please enter your NID"
                onChange={handleChange}
                isInvalid={!!errors.voter_id}
              />
              <Form.Control.Feedback type="invalid">
                {errors.voter_id}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleUpdate}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfilePage;
